import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useTranslation } from 'react-i18next';

const OrderSummary = ({ 
  subtotal, 
  shipping, 
  tax, 
  total, 
  onApplyPromoCode, 
  onProceedToCheckout,
  isLoading = false 
}) => {
  const { t } = useTranslation();
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError(t('promoCodeRequired'));
      return;
    }

    setIsApplyingPromo(true);
    setPromoError('');
    setPromoSuccess('');

    // Simulate API call
    setTimeout(() => {
      // Mock promo code validation
      const validCodes = ['SAVE10', 'WELCOME20', 'SUMMER15'];
      
      if (validCodes.includes(promoCode.toUpperCase())) {
        setPromoSuccess(t('promoCodeApplied', { code: promoCode }));
        onApplyPromoCode(promoCode);
      } else {
        setPromoError(t('invalidPromoCode'));
      }
      
      setIsApplyingPromo(false);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyPromo();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6 sticky top-20">
      <h2 className="text-xl font-semibold text-foreground mb-6">{t('orderSummary')}</h2>

      {/* Promo Code Section */}
      <div className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder={t('enterPromoCode')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              onKeyPress={handleKeyPress}
              error={promoError}
              className="mb-0"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleApplyPromo}
            loading={isApplyingPromo}
            disabled={!promoCode.trim()}
          >
            {t('apply')}
          </Button>
        </div>
        
        {promoSuccess && (
          <div className="flex items-center gap-2 mt-2 text-sm text-success">
            <Icon name="CheckCircle" size={16} />
            {promoSuccess}
          </div>
        )}
      </div>

      {/* Price Breakdown */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-foreground">
          <span>{t('subtotal')}</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-foreground">
          <span>{t('shipping')}</span>
          <span>{shipping === 0 ? t('free') : `$${shipping.toFixed(2)}`}</span>
        </div>
        
        <div className="flex justify-between text-foreground">
          <span>{t('tax')}</span>
          <span>${tax.toFixed(2)}</span>
        </div>
        
        {promoSuccess && (
          <div className="flex justify-between text-success">
            <span>{t('discount')}</span>
            <span>-$10.00</span>
          </div>
        )}
        
        <div className="border-t border-border pt-3">
          <div className="flex justify-between text-lg font-bold text-foreground">
            <span>{t('total')}</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Checkout Button */}
      <Button
        variant="default"
        size="lg"
        fullWidth
        onClick={onProceedToCheckout}
        loading={isLoading}
        iconName="ArrowRight"
        iconPosition="right"
        className="mb-4"
      >
        {t('proceedToCheckout')}
      </Button>

      {/* Continue Shopping */}
      <Button
        variant="ghost"
        size="sm"
        fullWidth
        iconName="ArrowLeft"
        iconPosition="left"
        onClick={() => window.history.back()}
      >
        {t('continueShopping')}
      </Button>
    </div>
  );
};

export default OrderSummary;
